import { upperFirst } from '@mantine/hooks';

import { useIssueState } from '../hooks/useIssueState';
import { OIncidentsStatus } from '../types';

import type { Schema } from '@/types/mercury-data-types/unifiedapi';
import type { ReactNode } from 'react';

import { StarIcon, DoneIcon, HourglassIcon, CheckIcon, CancelIcon } from '../icons/StatusIcons';

import classes from './IncidentState.module.css';

const statusConfig: Record<string, { icon: ReactNode; color: string; bg: string; label: string }> =
  {
    [OIncidentsStatus.New]: {
      icon: <StarIcon />,
      color: '#e61342',
      bg: 'rgba(230, 19, 66, 0.1)',
      label: 'New',
    },
    [OIncidentsStatus.Acknowledged]: {
      icon: <DoneIcon />,
      color: '#0dae2a',
      bg: 'rgba(13, 174, 42, 0.1)',
      label: 'Acknowledged',
    },
    [OIncidentsStatus.Pending]: {
      icon: <HourglassIcon />,
      color: '#0040ff',
      bg: 'rgba(0, 64, 255, 0.1)',
      label: 'Pending',
    },
    [OIncidentsStatus.Resolved]: {
      icon: <CheckIcon />,
      color: '#0dae2a',
      bg: 'rgba(13, 174, 42, 0.1)',
      label: 'Resolved',
    },
    [OIncidentsStatus.FalsePositive]: {
      icon: <CancelIcon />,
      color: '#777777',
      bg: 'rgba(163, 163, 163, 0.1)',
      label: 'False Positive',
    },
  };

interface IncidentStateProps {
  state?: Schema<'IssueClientResponse'>;
  hasTakedownRequests?: boolean;
}

const IncidentState = ({ state, hasTakedownRequests }: IncidentStateProps) => {
  const [currentStatus] = useIssueState(state, hasTakedownRequests);
  const config = statusConfig[currentStatus ?? OIncidentsStatus.New];

  if (!config) {
    return <span>{upperFirst(currentStatus ?? '')}</span>;
  }

  return (
    <span
      className={classes.chip}
      style={
        {
          '--chip-bg': config.bg,
          '--chip-color': config.color,
        } as React.CSSProperties
      }
    >
      <span className={classes.icon}>{config.icon}</span>
      {config.label}
    </span>
  );
};

export default IncidentState;
